import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

interface BreadcrumbSegment {
	name: string
	path: string
	isLast: boolean
}

const routeNameMap: Record<string, string> = {
	'': 'Inicio',
	profile: 'Perfil',
	Nuestra: 'Nosotros',
	Categoria: 'Categoría',
	Buscar: 'Buscar',
	Terminos: 'Términos y Condiciones',
	SubCategoria: 'Subcategoría',
	Producto: 'Producto',
	Login: 'Iniciar sesión',
	Registro: 'Registro',
	Promociones: 'Promociones',
	Pago: 'Pago',
	Carrito: 'Carrito',
	'under-construction': 'En construcción',
	'*': 'Página no encontrada',
}

const RouteHeader: React.FC = () => {
	const location = useLocation()
	const [segments, setSegments] = useState<BreadcrumbSegment[]>([])

	useEffect(() => {
		const pathSegments = location.pathname.split('/').filter(Boolean)

		const breadcrumbSegments: BreadcrumbSegment[] = pathSegments.map(
			(segment, index) => {
				const decodedSegment = decodeURIComponent(segment).replace(/-/g, ' ')

				const path = `/${pathSegments.slice(0, index + 1).join('/')}`
				const friendlyName =
					routeNameMap[decodedSegment] ||
					decodedSegment.charAt(0).toUpperCase() + decodedSegment.slice(1)

				return {
					name: friendlyName,
					path,
					isLast: index === pathSegments.length - 1,
				}
			}
		)

		setSegments(breadcrumbSegments)
	}, [location.pathname])

	const shouldNotRender =
		location.pathname === '/' ||
		location.pathname === '/home' ||
		segments.length === 0

	if (shouldNotRender) {
		return null
	}

	return (
		<div className='transition-all z-[1] duration-300 ease-in-out opacity-100 translate-y-0'>
			<nav
				className='hidden xl:flex items-center px-4 xl:px-20 py-2 bg-white border-b border-gray-100'
				aria-label='Breadcrumb'
			>
				<div className='flex items-center space-x-1 text-sm'>
					<Link
						to='/'
						className='group flex items-center text-gray-500 hover:text-red-600 transition-colors duration-200'
					>
						<svg
							className='w-4 h-4 mr-1.5 group-hover:scale-110 transition-transform duration-200'
							fill='none'
							stroke='currentColor'
							viewBox='0 0 24 24'
						>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
							/>
						</svg>
						<span className='font-medium'>Inicio</span>
					</Link>

					{segments.map((segment) => (
						<React.Fragment key={segment.path}>
							<svg
								className='w-4 h-4 text-gray-400'
								fill='none'
								stroke='currentColor'
								viewBox='0 0 24 24'
							>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M9 5l7 7-7 7'
								/>
							</svg>

							{segment.isLast ? (
								<span className='text-red-600 font-semibold px-2 py-1 bg-red-50 rounded-md'>
									{segment.name}
								</span>
							) : (
								<a
									className='text-gray-600 hover:text-red-600 font-medium px-2 py-1 rounded-md hover:bg-gray-100 transition-all duration-200'
								>
									{segment.name}
								</a>
							)}
						</React.Fragment>
					))}
				</div>
			</nav>

			<nav className='xl:hidden flex items-center justify-between px-4 py-2 bg-white border-b border-gray-100'>
				<a
					className='flex items-center text-gray-500 hover:text-red-600 transition-colors duration-200'
				>
					<svg
						className='w-4 h-4 mr-1'
						fill='none'
						stroke='currentColor'
						viewBox='0 0 24 24'
					>
						<path
							strokeLinecap='round'
							strokeLinejoin='round'
							strokeWidth={2}
							d='M15 19l-7-7 7-7'
						/>
					</svg>
					<span className='text-sm font-medium'>Volver</span>
				</a>

				<div className='flex items-center space-x-1 text-sm'>
					<span className='text-gray-400'>•</span>
					<span className='text-red-600 font-semibold'>
						{segments[segments.length - 1]?.name || 'Página'}
					</span>
				</div>

				<div className='w-8' />
			</nav>
		</div>
	)
}

export default RouteHeader
